import { useCallback, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  AppBar, Box, Toolbar, Typography, Button,
  IconButton, Menu, MenuItem, Divider, Tooltip, Avatar, Chip,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HandymanIcon from '@mui/icons-material/Handyman';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import MobileIcon from '@mui/icons-material/PhoneAndroid';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { USER_ROLES, USER_ROLE_TRANSLATIONS } from '../../models/School';
import NotificationBell from '../notifications/NotificationBell';

// ─── Role badge config ────────────────────────────────────────
const ROLE_CONFIG = {
  [USER_ROLES.SUPER_ADMIN]: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: <AdminPanelSettingsIcon sx={{ fontSize: 13 }} /> },
  [USER_ROLES.ADMIN]:       { color: '#3b5bdb', bg: 'rgba(59,91,219,0.12)',  icon: <SchoolIcon sx={{ fontSize: 13 }} /> },
  [USER_ROLES.TECHNICAL]:   { color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: <HandymanIcon sx={{ fontSize: 13 }} /> },
  [USER_ROLES.SERVICE]:     { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',  icon: <BuildIcon sx={{ fontSize: 13 }} /> },
  [USER_ROLES.REQUESTER]:   { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', icon: <PersonIcon sx={{ fontSize: 13 }} /> },
};

function NavButton({ to, icon, label, active }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={icon}
      sx={{
        color: active ? '#3b5bdb' : 'inherit',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 600,
        fontSize: '0.875rem',
        letterSpacing: '-0.01em',
        px: 1.75,
        py: 0.75,
        borderRadius: '10px',
        position: 'relative',
        transition: 'all 0.2s ease',
        background: active ? 'rgba(59,91,219,0.08)' : 'transparent',
        '&:hover': {
          background: 'rgba(59,91,219,0.06)',
          color: '#3b5bdb',
        },
        '&::after': active ? {
          content: '""',
          position: 'absolute',
          bottom: 2,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '20px',
          height: '3px',
          borderRadius: '99px',
          background: 'linear-gradient(90deg, #3b5bdb, #5c7cfa)',
        } : {},
      }}
    >
      {label}
    </Button>
  );
}

export default function Navbar() {
  const { currentUser, logout, userRole, userService, userSchool } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const isLoggingOut = useRef(false);

  const isActive = useCallback((path, exact = false) => {
    if (exact) return location.pathname === path;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const handleLogout = async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
    } finally {
      isLoggingOut.current = false;
    }
  };

  if (!currentUser) return null;

  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG[USER_ROLES.REQUESTER];
  const roleLabel  = USER_ROLE_TRANSLATIONS[userRole] || userRole;
  const entityName = userService?.name || userSchool?.name || '';
  const initials   = (currentUser?.email || '?')[0].toUpperCase();

  return (
    <AppBar position="fixed" elevation={0}>
      <Toolbar sx={{ minHeight: { xs: 60, sm: 64 }, gap: 1, px: { xs: 2, sm: 3 } }}>

        {/* ── Logo ─────────────────────────────────────────── */}
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textDecoration: 'none',
            mr: { xs: 1, md: 3 },
            flexShrink: 0,
          }}
        >
          <Box sx={{
            width: 32, height: 32,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b5bdb, #5c7cfa)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(59,91,219,0.4)',
            flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 9L12 3L21 9V21H15V15H9V21H3V9Z" stroke="white" strokeWidth="2.2" strokeLinejoin="round" />
            </svg>
          </Box>
          <Typography sx={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            fontSize: '1.1rem',
            letterSpacing: '-0.03em',
            color: darkMode ? '#e8eeff' : '#0c1120',
            display: { xs: 'none', sm: 'block' },
          }}>
            EduWorks
          </Typography>

          {entityName && (
            <Chip
              label={entityName}
              size="small"
              sx={{
                display: { xs: 'none', lg: 'flex' },
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                background: roleConfig.bg,
                color: roleConfig.color,
                border: `1px solid ${roleConfig.color}22`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* ── Nav links ────────────────────────────────────── */}
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <NavButton to="/"            label="Tableau de bord" icon={<DashboardIcon sx={{ fontSize: 17 }} />} active={isActive('/', true)} />
          <NavButton to="/demandes"    label="Demandes"        active={isActive('/demandes')} />
          <NavButton to="/planification" label="Planification" icon={<CalendarMonthIcon sx={{ fontSize: 17 }} />} active={isActive('/planification')} />

          {(userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN) && (
            <>
              <NavButton to="/services" label="Services" icon={<BuildIcon sx={{ fontSize: 17 }} />} active={isActive('/services')} />
              <NavButton to="/secteurs" label="Secteurs QR" icon={<QrCode2Icon sx={{ fontSize: 17 }} />} active={isActive('/secteurs')} />
            </>
          )}
        </Box>

        {/* ── Right actions ─────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NotificationBell />

          <Tooltip title={darkMode ? 'Mode clair' : 'Mode sombre'}>
            <IconButton
              onClick={toggleDarkMode}
              size="small"
              sx={{
                color: darkMode ? '#8fa3cc' : '#4a5568',
                borderRadius: '10px',
                '&:hover': { background: 'rgba(59,91,219,0.08)' },
              }}
            >
              {darkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Mon compte">
            <IconButton
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              size="small"
              sx={{ ml: 0.5, p: 0.5 }}
            >
              <Avatar sx={{
                width: 34, height: 34,
                background: `linear-gradient(135deg, ${roleConfig.color}, ${roleConfig.color}99)`,
                fontSize: '0.85rem',
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
              }}>
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── User menu ─────────────────────────────────────── */}
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 260, mt: 1 } } }}
        >
          {/* User info header */}
          <Box sx={{ px: 2, py: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{
                width: 40, height: 40,
                background: `linear-gradient(135deg, ${roleConfig.color}, ${roleConfig.color}99)`,
                fontSize: '1rem',
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
              }}>
                {initials}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
                  {currentUser?.displayName || currentUser?.email}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <Box sx={{ color: roleConfig.color, display: 'flex', alignItems: 'center' }}>
                    {roleConfig.icon}
                  </Box>
                  <Typography variant="caption" sx={{ color: roleConfig.color, fontWeight: 600 }}>
                    {roleLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mx: 1 }} />

          <MenuItem onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
            <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={500}>Mon profil</Typography>
          </MenuItem>

          {userRole === USER_ROLES.SUPER_ADMIN && [
            <MenuItem key="admin" component={RouterLink} to="/admin-dashboard" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <AdminPanelSettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Gestion administrateurs</Typography>
            </MenuItem>,
            <MenuItem key="schools" component={RouterLink} to="/schools" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <SchoolIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Gestion des écoles</Typography>
            </MenuItem>,
            <MenuItem key="secteurs-sa" component={RouterLink} to="/secteurs" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <QrCode2Icon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Secteurs QR</Typography>
            </MenuItem>,
            <Divider key="div-sa" sx={{ mx: 1 }} />,
          ]}

          {userRole === USER_ROLES.ADMIN && [
            <MenuItem key="secteurs-admin" component={RouterLink} to="/secteurs" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <QrCode2Icon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Secteurs QR</Typography>
            </MenuItem>,
            <Divider key="div-admin" sx={{ mx: 1 }} />,
          ]}

          {userRole === USER_ROLES.TECHNICAL_SERVICE && [
            <MenuItem key="analytics" component={RouterLink} to="/service-analytics" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <AnalyticsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Analyses & statistiques</Typography>
            </MenuItem>,
            <MenuItem key="mobile" component={RouterLink} to="/service-mobile" onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
              <MobileIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>Version mobile</Typography>
            </MenuItem>,
            <Divider key="div-tech" sx={{ mx: 1 }} />,
          ]}

          <MenuItem onClick={() => setMenuAnchorEl(null)} sx={{ gap: 1.5 }}>
            <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={500}>Paramètres</Typography>
          </MenuItem>

          <Divider sx={{ mx: 1 }} />

          <MenuItem
            onClick={() => { setMenuAnchorEl(null); handleLogout(); }}
            sx={{ gap: 1.5, color: 'error.main', mx: 1, mb: 0.5, borderRadius: '8px' }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Déconnexion</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
